import React, {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import {Login as LoginIcon, Visibility, VisibilityOff} from '@mui/icons-material';
import {authService} from '@/services/authService';

export const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get the redirect path from location state, or default to dashboard
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            // Validate input
            if (!username.trim() || !password) {
                throw new Error('Please enter both username and password');
            }

            // Attempt login
            await authService.login({
                username: username.trim(),
                password,
            });

            // Redirect to the page user was trying to access
            navigate(from, {replace: true});
        } catch (err) {
            console.error('Login error:', err);

            // Handle different error types
            const error = err as { response?: { status: number; data?: { detail?: string } }; message?: string };
            if (error.response?.status === 401) {
                setError('Invalid username or password');
            } else if (error.response?.status === 403) {
                setError('Your account has been deactivated');
            } else if (error.response?.status === 429) {
                setError('Too many login attempts. Please try again later');
            } else if (error.message) {
                setError(error.message);
            } else {
                setError('Login failed. Please try again');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Card sx={{width: '100%', boxShadow: 3}}>
                    <CardContent sx={{p: 4}}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                mb: 3,
                            }}
                        >
                            <LoginIcon sx={{fontSize: 48, color: 'primary.main', mb: 2}}/>
                            <Typography component="h1" variant="h5" fontWeight="bold">
                                InstaPublisher
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                Sign in to continue
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{mb: 2}}>
                                {error}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="Username"
                                name="username"
                                autoComplete="username"
                                autoFocus
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={isLoading}
                                error={!!error}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                                error={!!error}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={isLoading}
                                            >
                                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{mt: 3, mb: 2, py: 1.5}}
                                disabled={isLoading || !username.trim() || !password}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit"/> : 'Sign In'}
                            </Button>
                        </Box>

                        {/* Development mode hint */}
                        {process.env.NODE_ENV === 'development' && (
                            <Box sx={{mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1}}>
                                <Typography variant="caption" color="text.secondary">
                                    Development Mode: Use admin/admin123 for testing
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Typography variant="body2" color="text.secondary" align="center" sx={{mt: 2}}>
                    &copy; {new Date().getFullYear()} InstaPublisher. All rights reserved.
                </Typography>
            </Box>
        </Container>
    );
};
